/* eslint-disable react/prop-types */
import styles from "./chip.module.scss";
import CustomLink from "@Components/CustomLink";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import useIsDesktop from "hooks/useIsDesktop";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import { useSelector, shallowEqual } from "react-redux";
import { AppState } from "@Store/index";
import { useMemo } from "react";
import config from "@Config/index";
import { CANNIBALIZATION_ROLLOUT } from "constants/configConstants";

type ChipTypes = {
  href: string;
  value: string;
  canonical_name: string;
  dataTestId?: string;
  groupName?: string;
};

const Chip = ({ href, value, canonical_name, dataTestId, groupName }: ChipTypes) => {
  const isDesktop = useIsDesktop();
  const { t } = useTranslationHook();
  const { countryCode } = useSelector(
    ({ appData }: AppState) => ({
      countryCode: appData.countryCode,
    }),
    shallowEqual,
  );
  const getInfusedPrefix = () => {
    if (config.IS_CHINA_APP) {
      return "";
    }
    const UK_AU_IE_DE_ES = ["GB", "AU", "IE", "DE", "ES"];
    const US_CA = ["US", "CA"];

    if (UK_AU_IE_DE_ES.includes(countryCode)) {
      if (groupName === "cities")
        return t("searchpage.components.nearbysection.chipsection.studentaccommodationin");
      // cannibalization check
      if (groupName === "universities")
        return CANNIBALIZATION_ROLLOUT.includes(countryCode || "")
          ? ""
          : t("searchpage.components.nearbysection.chipsection.studentaccommodationnear");
      if (groupName === "areas")
        return t("searchpage.components.nearbysection.chipsection.studentaccommodationnear");
      if (groupName === t("searchpage.components.nearbysection.propertiespanel")) return "";
    } else if (US_CA.includes(countryCode)) {
      if (groupName === "cities")
        return t("searchpage.components.nearbysection.chipsection.studenthousingin");
      if (groupName === t("searchpage.components.nearbysection.propertiespanel")) return "";
      if (groupName === "areas")
        return t("searchpage.components.nearbysection.chipsection.studenthousingnear");
      if (groupName === "universities") {
        return countryCode === "US"
          ? t("searchpage.components.nearbysection.chipsection.apartmentsnear")
          : t("searchpage.components.nearbysection.chipsection.offcampushousingnear");
      }
    }

    return groupName === t("searchpage.components.nearbysection.propertiespanel")
      ? ""
      : t("searchpage.components.nearbysection.chipsection.studentaccommodationnear");
  };
  const infusedPrefix = useMemo(() => getInfusedPrefix(), [countryCode, groupName]);

  return (
    <CustomLink
      href={href}
      className={formatClassNames(styles.chip, isDesktop ? styles.chipDesktop : "")}
      target={isDesktop ? "_self" : "_blank"}
      rel="noopener noreferrer"
      isExternal={!isDesktop}
      onClick={() => {
        gtagEventEmitter({
          action: "clicked_places_to_visit_nearby",
          canonical_name,
        });
      }}
      dataTestId={dataTestId}
    >
      {infusedPrefix} {t(value)}
    </CustomLink>
  );
};

export default wrapperHOC(Chip, {
  componentName: "Chip-NearbySection-SearchPage",
  showForChina: true,
});
